import { useApi } from '@mainstage/core-plugin-api';
import { CatalogApi, catalogApiRef } from '@mainstage/plugin-catalog-react';
import { useQuery } from '@tanstack/react-query';
import pLimit from 'p-limit';
import { useMemo } from 'react';
import { expandEntityRefSafe } from '@agilelab/plugin-wb-platform-common';

const STALE_TIME = 10 * 60 * 1000; // 10 minutes

// ---------------------------------------------------------------------------
// Constants – change these to tune catalog lookup behaviour
// ---------------------------------------------------------------------------

/** Number of entity refs sent per catalog batch request. */
export const ENTITY_DISPLAY_NAME_BATCH_SIZE = 100;

/** Maximum number of concurrent batch requests to the catalog. */
export const ENTITY_DISPLAY_NAME_CONCURRENCY = 5;

type EntityToResolve = { originalRef: string; expandedRef: string };

/**
 * Resolves a list of refs to
 * human-readable display names via the Backstage catalog API.
 *
 * Handles the following identity formats:
 *  - Full entity ref:   "user:default/john.doe"
 *  - Short entity ref:  "user:dmbtest_agilelab.it"  (no namespace/name segment)
 *
 * and normalizes both to full entity ref before passing to the catalog
 *
 *
 * Non-ref strings are passed through unchanged.
 * Entity refs not found in the catalog fall back to their original string.
 * A failed batch does not prevent other batches from resolving.
 *
 * Batch size and concurrency are controlled by the constants ENTITY_DISPLAY_NAME_BATCH_SIZE and ENTITY_DISPLAY_NAME_CONCURRENCY.
 */
async function resolveIdentitiesDisplayNames(
  identitiesRefs: string[],
  catalogApi: CatalogApi,
): Promise<Map<string, string>> {
  const resultMap = new Map<string, string>();

  const refs: EntityToResolve[] = [];
  const nonRefs: string[] = [];

  for (const identity of identitiesRefs) {
    const expandedRef = expandEntityRefSafe(identity);
    if (expandedRef !== null)
      refs.push({ originalRef: identity, expandedRef: expandedRef });
    else nonRefs.push(identity);
  }

  // Non-refs pass through as-is
  for (const nonRef of nonRefs) {
    resultMap.set(nonRef, nonRef);
  }

  if (refs.length === 0) return resultMap;

  // Split into batches
  const batches: EntityToResolve[][] = [];
  for (let i = 0; i < refs.length; i += ENTITY_DISPLAY_NAME_BATCH_SIZE) {
    batches.push(refs.slice(i, i + ENTITY_DISPLAY_NAME_BATCH_SIZE));
  }

  const limit = pLimit(ENTITY_DISPLAY_NAME_CONCURRENCY);

  await Promise.all(
    batches.map(batch =>
      limit(async () => {
        try {
          const { items } = await catalogApi.getEntitiesByRefs({
            entityRefs: batch.map(item => item.expandedRef),
            fields: ['metadata.name', 'spec.profile'],
          });
          items.forEach((item, idx) => {
            const entity = batch[idx];
            const displayName = item
              ? (item.spec?.profile as Record<string, string>)?.displayName ??
                item.metadata?.name
              : undefined;
            resultMap.set(
              entity.originalRef,
              displayName ?? entity.originalRef,
            );
          });
        } catch {
          // Batch failed – fall back for all refs in this batch individually
          for (const entity of batch) {
            resultMap.set(entity.originalRef, entity.originalRef);
          }
        }
      }),
    ),
  );

  return resultMap;
}

/**
 * React Query hook that resolves an arbitrary list of identity strings
 * (entity refs, plain strings, mixed) to display names.
 *
 * Results are cached for 10 minutes. The query key is stable as long as
 * the same set of identities is passed, so filter-count changes that don't
 * alter the identity set won't trigger a re-fetch.
 */
export function useIdentitiesDisplayNames(identitiesRefs: string[]) {
  const catalogApi = useApi(catalogApiRef);

  // Sort + deduplicate for a stable query key
  const stableIds = useMemo(() => {
    const uniqueIdentities = [...new Set(identitiesRefs)].sort();
    return { uniqueIdentities, hash: uniqueIdentities.join('\x00') };
  }, [identitiesRefs]);

  const result = useQuery({
    queryKey: ['identities-display-names', stableIds.hash],
    queryFn: () =>
      resolveIdentitiesDisplayNames(stableIds.uniqueIdentities, catalogApi),
    staleTime: STALE_TIME,
  });

  return result;
}
